import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Input,
  Typography,
  message,
  Space,
  Tooltip,
  Table,
  DatePicker,
  Modal
} from "antd";
import {
  KeyOutlined,
  CopyOutlined,
  DeleteOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";

const { Text, Title } = Typography;
const { confirm } = Modal;

export default function Token() {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tokensGuardados, setTokensGuardados] = useState([]);
  const [fechaExpiracion, setFechaExpiracion] = useState(null);
  const [horaServidor, setHoraServidor] = useState(null);

  const usuarioMicrosoft = localStorage.getItem("usuarioMicrosoft") || "admin";

  useEffect(() => {
    const fetchHoraServidor = async () => {
      try {
        const res = await axios.get("/api/hora-servidor");
        setHoraServidor(new Date(res.data.ahora_utc).getTime());
      } catch (e) {
        message.error("No se pudo obtener la hora del servidor");
      }
    };

    fetchHoraServidor();
  }, []);

  useEffect(() => {
    if (horaServidor) {
      obtenerHistorialDesdeBackend();
    }
  }, [horaServidor]);

  const calcularTiempoRestante = (ms) => {
    if (ms <= 0) return "Expirado";
    const minutos = Math.floor((ms / 1000 / 60) % 60);
    const horas = Math.floor((ms / 1000 / 60 / 60) % 24);
    const dias = Math.floor(ms / 1000 / 60 / 60 / 24);
    return `${dias}d ${horas}h ${minutos}m`;
  };

  const obtenerHistorialDesdeBackend = async () => {
    try {
      const res = await axios.get("/launcher/tokens-historial");
      const tokens = res.data.map((t) => {
        const fechaExpira = new Date(t.expira).getTime();
        const expirado = horaServidor > fechaExpira;
        const tiempoRestanteMs = fechaExpira - horaServidor;
        return {
          ...t,
          expirado,
          tiempoRestante: calcularTiempoRestante(tiempoRestanteMs)
        };
      });
      setTokensGuardados(tokens);
    } catch (err) {
      console.error("Error al obtener tokens guardados:", err);
      message.error("No se pudo cargar el historial de tokens");
    }
  };

  const generarToken = async () => {
    if (!fechaExpiracion) {
      message.warning("Selecciona una fecha de expiración antes de generar el token");
      return;
    }

    setLoading(true);
    setCopied(false);
    try {
      const res = await axios.post("/api/launcher-token", {
        usuario: usuarioMicrosoft,
        expira: fechaExpiracion.toISOString()
      });

      if (res.data?.token) {
        setToken(res.data.token);
        message.success("Token generado correctamente");
        setFechaExpiracion(null);
        await obtenerHistorialDesdeBackend();
      } else {
        message.error("No se recibió token del backend");
      }
    } catch (err) {
      console.error("❌ Error al generar token:", err);
      message.error("❌ Error al obtener el token");
    } finally {
      setLoading(false);
    }
  };

  const copiarToken = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      message.success("Token copiado al portapapeles");
    } catch {
      message.error("No se pudo copiar el token");
    }
  };

  const confirmarEliminacion = (tokenObj) => {
    confirm({
      title: "¿Está seguro de eliminar este token?",
      content: `Token: ${tokenObj.token.slice(0, 20)}...`,
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await axios.delete(`/launcher/eliminar-token?token=${tokenObj.token}`);
          message.success("Token eliminado");
          await obtenerHistorialDesdeBackend();
        } catch (err) {
          message.error("No se pudo eliminar el token");
        }
      }
    });
  };

  const columnas = [
    {
      title: "Usuario",
      dataIndex: "usuario",
      key: "usuario"
    },
    {
      title: "Generado",
      dataIndex: "generado",
      key: "generado",
      render: (text) => dayjs(text).format("DD/MM/YYYY HH:mm")
    },
    {
      title: "Expira",
      dataIndex: "expira",
      key: "expira",
      render: (text) => dayjs(text).format("DD/MM/YYYY HH:mm")
    },
    {
      title: "Quedan",
      dataIndex: "tiempoRestante",
      key: "tiempoRestante"
    },
    {
      title: "Estado",
      key: "expirado",
      render: (_, record) =>
        record.expirado ? (
          <CloseCircleTwoTone twoToneColor="#ff4d4f" />
        ) : (
          <CheckCircleTwoTone twoToneColor="#52c41a" />
        )
    },
    {
      title: "Acciones",
      key: "acciones",
      render: (_, record) => (
        <Tooltip title="Eliminar token">
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => confirmarEliminacion(record)}
          />
        </Tooltip>
      )
    }
  ];

  return (
    <div style={{ padding: "0rem 1rem 1rem", maxWidth: 1000, margin: "0 auto" }}>
      <Card bordered>
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <Title level={4}>🔐 Generar Token Interno</Title>
          <Text>
            Este token puede ser usado en Power Automate para autenticar llamadas al backend.
          </Text>

          <Space>
            <DatePicker
              showTime
              value={fechaExpiracion}
              onChange={setFechaExpiracion}
              placeholder="Fecha de expiración"
            />
            <Button
              type="primary"
              icon={<KeyOutlined />}
              onClick={generarToken}
              loading={loading}
            >
              Generar token
            </Button>
          </Space>

          <Input
            size="large"
            value={token}
            readOnly
            placeholder="Aún no has generado un token"
            style={{ fontFamily: "monospace", background: "#f9f9f9" }}
            suffix={
              <Tooltip title={copied ? "Copiado" : "Copiar"}>
                <Button
                  icon={<CopyOutlined />}
                  onClick={copiarToken}
                  disabled={!token}
                />
              </Tooltip>
            }
          />

          <Title level={5}>🧾 Historial de Tokens Generados</Title>

          <Table
            dataSource={tokensGuardados}
            columns={columnas}
            rowKey={(record) => record.token}
            pagination={{ pageSize: 5 }}
          />
        </Space>
      </Card>
    </div>
  );
}
